import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthLegacy } from '@/components/hooks/useAuth';
import { AuthStateContext } from './AuthStateContext';
import TokenValidationService from '@/services/TokenValidationService';
import TokenExpiryNotification from './TokenExpiryNotification';
import SessionExpiryWarningModal from './SessionExpiryWarningModal';
import { checkBuyerExists, syncAuth0Buyer } from '@/utils/api';
import { getLogger } from '@/utils/logger';
import { toast } from 'react-toastify';

const log = getLogger('AuthStateProvider');
const NORMAL_TOKEN_CHECK_MS = 300000;
const EXPIRY_TOKEN_CHECK_MS = 30000;
const EXPIRY_WARNING_MS = 2 * 60 * 1000;
const DISMISS_SUPPRESSION_MS = 120000;
const TOKEN_CACHE_TTL_MS = 5000;

const AUTH_LIFECYCLE_STATES = {
  BOOTING: 'booting',
  UNAUTHENTICATED: 'unauthenticated',
  TOKEN_READY: 'token_ready',
  TOKEN_REFRESHING: 'token_refreshing',
  PROFILE_SYNCING: 'profile_syncing',
  SESSION_EXPIRED: 'session_expired',
  READY: 'ready',
  ERROR: 'error',
};

const RESOLVED_STATES = new Set([
  AUTH_LIFECYCLE_STATES.UNAUTHENTICATED,
  AUTH_LIFECYCLE_STATES.SESSION_EXPIRED,
  AUTH_LIFECYCLE_STATES.READY,
  AUTH_LIFECYCLE_STATES.ERROR,
]);

const nowMs = () =>
  (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();

const isProfileComplete = (dbBuyer) =>
  Boolean(
    dbBuyer?.firstName &&
      dbBuyer?.lastName &&
      dbBuyer?.email
  );

const extractNameParts = (auth0User) => {
  const names = auth0User?.name ? auth0User.name.split(' ') : [];
  const nameIsEmail = Boolean(auth0User?.name && auth0User.name.includes('@'));
  const firstName = nameIsEmail ? '' : (auth0User?.given_name || (names.length > 0 ? names[0] : ''));
  const lastName = nameIsEmail ? '' : (auth0User?.family_name || (names.length > 1 ? names.slice(1).join(' ') : ''));
  return { firstName, lastName };
};

export const AuthStateProvider = ({ children }) => {
  const authState = useAuthLegacy();
  const location = useLocation();
  const [showExpiryNotification, setShowExpiryNotification] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [validationIntervalMs, setValidationIntervalMs] = useState(NORMAL_TOKEN_CHECK_MS);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [isProfileSyncing, setIsProfileSyncing] = useState(false);
  const [profileSyncCompleted, setProfileSyncCompleted] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authLifecycleState, setAuthLifecycleState] = useState(AUTH_LIFECYCLE_STATES.BOOTING);
  const [authTransitionCount, setAuthTransitionCount] = useState(0);
  const [authBootstrapDurationMs, setAuthBootstrapDurationMs] = useState(null);
  const [tokenAcquisitionCount, setTokenAcquisitionCount] = useState(0);
  const [profileSyncCount, setProfileSyncCount] = useState(0);

  const showExpiryNotificationRef = useRef(showExpiryNotification);
  const showExpiryWarningRef = useRef(showExpiryWarning);
  const suppressExpiryNotificationUntilRef = useRef(0);
  const suppressExpiryWarningUntilRef = useRef(0);
  const previousUserSubRef = useRef(authState.auth0User?.sub || null);
  const latestAuthRef = useRef({
    isAuthenticated: authState.isAuthenticated,
    userSub: authState.auth0User?.sub || null,
  });
  const lifecycleStateRef = useRef(AUTH_LIFECYCLE_STATES.BOOTING);
  const bootstrapStartRef = useRef(nowMs());
  const profileSyncKeyRef = useRef(null);
  const tokenRef = useRef({
    value: null,
    acquiredAt: 0,
    inFlightPromise: null,
  });
  const validateTokenRef = useRef(null);

  useEffect(() => {
    showExpiryNotificationRef.current = showExpiryNotification;
  }, [showExpiryNotification]);

  useEffect(() => {
    showExpiryWarningRef.current = showExpiryWarning;
  }, [showExpiryWarning]);

  useEffect(() => {
    latestAuthRef.current = {
      isAuthenticated: authState.isAuthenticated,
      userSub: authState.auth0User?.sub || null,
    };
  }, [authState.isAuthenticated, authState.auth0User?.sub]);

  const getProviderTokenOrThrow = useCallback(
    async (options = undefined) => {
      if (!authState.isAuthenticated) {
        throw new Error('Account is not authenticated');
      }

      const hasCustomOptions = Boolean(options && Object.keys(options).length > 0);
      const tokenSnapshot = tokenRef.current;
      const tokenIsFresh =
        Boolean(tokenSnapshot.value) &&
        (Date.now() - tokenSnapshot.acquiredAt) < TOKEN_CACHE_TTL_MS;

      if (!hasCustomOptions && tokenIsFresh) {
        return tokenSnapshot.value;
      }

      if (!hasCustomOptions && tokenSnapshot.inFlightPromise) {
        return tokenSnapshot.inFlightPromise;
      }

      const tokenPromise = (async () => {
        const token = await authState.getTokenOrThrow(options);
        setTokenAcquisitionCount((count) => count + 1);
        if (!hasCustomOptions) {
          tokenRef.current.value = token;
          tokenRef.current.acquiredAt = Date.now();
        }
        return token;
      })();

      if (!hasCustomOptions) {
        tokenRef.current.inFlightPromise = tokenPromise;
      }

      try {
        return await tokenPromise;
      } finally {
        if (!hasCustomOptions && tokenRef.current.inFlightPromise === tokenPromise) {
          tokenRef.current.inFlightPromise = null;
        }
      }
    },
    [authState.getTokenOrThrow, authState.isAuthenticated]
  );

  const getProviderToken = useCallback(
    async (options = undefined) => {
      try {
        return await getProviderTokenOrThrow(options);
      } catch (error) {
        log.error(
          `[AuthStateProvider:getProviderToken] > [Request]: acquire token > [Response]: error=${error?.message || 'unknown'}`,
          error
        );
        return null;
      }
    },
    [getProviderTokenOrThrow]
  );

  const validateToken = useCallback(async (options = {}) => {
    if (authState.isLoading) {
      return { isValid: false, requiresLogin: false, reason: 'auth_loading' };
    }
    if (!authState.isAuthenticated) {
      return { isValid: false, requiresLogin: true, reason: 'unauthenticated' };
    }
    const sessionUserSub = authState.auth0User?.sub || null;

    setIsValidatingToken(true);
    try {
      const result = await TokenValidationService.validateToken(
        getProviderTokenOrThrow,
        authState.isAuthenticated,
        options
      );
      const latestAuth = latestAuthRef.current;
      if (!latestAuth.isAuthenticated || latestAuth.userSub !== sessionUserSub) {
        return result;
      }

      if (result.requiresLogin) {
        setSessionExpired(true);
        tokenRef.current.value = null;
        tokenRef.current.acquiredAt = 0;
        tokenRef.current.inFlightPromise = null;
      } else if (result.isValid) {
        setSessionExpired(false);
        setAuthError(null);
      }

      if (!result.isValid && !result.requiresLogin && result.error) {
        setAuthError(result.error);
      }

      const suppressionExpired = Date.now() >= suppressExpiryNotificationUntilRef.current;
      const warningSuppressionExpired = Date.now() >= suppressExpiryWarningUntilRef.current;

      // Handle expiry warning (< 2 minutes, not expired yet)
      if (
        result.warningOnly &&
        !result.requiresLogin &&
        !showExpiryWarningRef.current &&
        warningSuppressionExpired
      ) {
        setShowExpiryWarning(true);
        setTimeUntilExpiry(result.timeUntilExpiry);
        log.info(
          '[AuthStateProvider:validateToken] > Showing expiry warning, timeUntilExpiry=' + result.timeUntilExpiry
        );
      }

      // Update time if warning already shown
      if (showExpiryWarningRef.current && result.timeUntilExpiry) {
        setTimeUntilExpiry(result.timeUntilExpiry);
      }

      // Clear warning if token refreshed and has > 2 minutes
      if (showExpiryWarningRef.current && result.isValid && !result.warningOnly) {
        setShowExpiryWarning(false);
        setTimeUntilExpiry(null);
        log.info('[AuthStateProvider:validateToken] > Session extended, clearing warning');
      }

      // Handle actual expiry
      if (
        result.requiresLogin &&
        !showExpiryNotificationRef.current &&
        suppressionExpired
      ) {
        setShowExpiryNotification(true);
        setShowExpiryWarning(false); // Hide warning when actually expired
        log.info('[AuthStateProvider:validateToken] > Session expired');
      }

      // Dynamic cadence:
      // - Fast checks while within warning window.
      // - Otherwise, schedule next check around when we enter warning window.
      if (typeof result.timeUntilExpiry === 'number' && result.timeUntilExpiry > 0) {
        const msUntilWarningWindow = result.timeUntilExpiry - EXPIRY_WARNING_MS;
        if (msUntilWarningWindow <= 0) {
          setValidationIntervalMs(EXPIRY_TOKEN_CHECK_MS);
        } else {
          const nextIntervalMs = Math.min(
            NORMAL_TOKEN_CHECK_MS,
            Math.max(EXPIRY_TOKEN_CHECK_MS, msUntilWarningWindow + 1000)
          );
          setValidationIntervalMs(nextIntervalMs);
        }
      } else {
        setValidationIntervalMs(NORMAL_TOKEN_CHECK_MS);
      }

      return result;
    } catch (error) {
      setAuthError(error?.message || 'token_validation_failed');
      log.error(
        `[AuthStateProvider:validateToken] > [Request]: validate token > [Response]: error=${error?.message || 'unknown'}`,
        error
      );
      return { isValid: false };
    } finally {
      setIsValidatingToken(false);
    }
  }, [
    authState.isAuthenticated,
    authState.isLoading,
    authState.auth0User?.sub,
    getProviderTokenOrThrow,
  ]);

  useEffect(() => {
    validateTokenRef.current = validateToken;
  }, [validateToken]);

  const handleCloseNotification = useCallback(() => {
    setShowExpiryNotification(false);
    suppressExpiryNotificationUntilRef.current = Date.now() + DISMISS_SUPPRESSION_MS;
    setValidationIntervalMs(NORMAL_TOKEN_CHECK_MS);
  }, []);

  const handleExtendSession = useCallback(async () => {
    try {
      log.info('[AuthStateProvider:handleExtendSession] > Requesting token refresh');
      const newToken = await getProviderTokenOrThrow({
        cacheMode: 'off',
        ignoreCache: true,
        timeoutInSeconds: 10,
      });
      tokenRef.current.value = newToken;
      tokenRef.current.acquiredAt = Date.now();
      tokenRef.current.inFlightPromise = null;
      TokenValidationService.resetValidation();
      const validationResult = await validateToken({ forceFresh: true });
      if (validationResult.requiresLogin) {
        setShowExpiryWarning(false);
        setSessionExpired(true);
        setShowExpiryNotification(true);
        log.error(
          '[AuthStateProvider:handleExtendSession] > Refresh token expired, showing expiry notification'
        );
        return;
      }

      if (validationResult.warningOnly) {
        toast.warning('Session is still close to expiry. Please try extending again.', {
          position: 'bottom-right',
          autoClose: 4000,
        });
        return;
      }

      suppressExpiryWarningUntilRef.current = Date.now() + DISMISS_SUPPRESSION_MS;
      setShowExpiryWarning(false);
      setTimeUntilExpiry(null);

      toast.success('Session extended successfully', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (error) {
      log.error('[AuthStateProvider:handleExtendSession] > Failed to extend session', error);
      const isLoginRequired =
        error.message?.includes('Missing Refresh Token') ||
        error.message?.includes('login_required') ||
        error.message?.includes('Login required') ||
        error.error === 'login_required' ||
        error.error === 'consent_required';

      if (isLoginRequired) {
        setShowExpiryWarning(false);
        setSessionExpired(true);
        setShowExpiryNotification(true);
        log.error(
          '[AuthStateProvider:handleExtendSession] > Refresh token expired, showing expiry notification'
        );
      } else {
        toast.error(`Failed to extend session: ${error.message || 'Unknown error'}`, {
          position: 'bottom-right',
          autoClose: 5000,
        });
      }
    }
  }, [getProviderTokenOrThrow, validateToken]);

  const handleDismissWarning = useCallback(() => {
    setShowExpiryWarning(false);
    // Suppress briefly so it can reappear if user does not extend session.
    suppressExpiryWarningUntilRef.current = Date.now() + 30000;
    log.info('[AuthStateProvider:handleDismissWarning] > Warning dismissed');
  }, []);

  useEffect(() => {
    log.info(
      `[AuthStateProvider] > [Response]: state updated > [Comment]: isAuthenticated=${authState.isAuthenticated} isLoading=${authState.isLoading}`
    );
  }, [authState.isAuthenticated, authState.isLoading]);

  useEffect(() => {
    if (authState.isLoading) {
      return;
    }
    validateTokenRef.current?.();
  }, [location.pathname, authState.isLoading]);

  useEffect(() => {
    if (!authState.isAuthenticated || authState.isLoading) {
      return undefined;
    }

    console.log('[AuthStateProvider] Setting up validation interval:', {
      intervalMs: validationIntervalMs,
      intervalDescription:
        validationIntervalMs === EXPIRY_TOKEN_CHECK_MS
          ? '30 seconds (within warning window)'
          : `dynamic (${validationIntervalMs}ms)`,
    });

    const intervalId = setInterval(() => {
      console.log('[AuthStateProvider] Validation interval triggered, calling validateToken()');
      validateTokenRef.current?.();
    }, validationIntervalMs);

    return () => {
      console.log('[AuthStateProvider] Clearing validation interval');
      clearInterval(intervalId);
    };
  }, [
    authState.isAuthenticated,
    authState.isLoading,
    validationIntervalMs,
  ]);

  useEffect(() => {
    const handleFocus = () => {
      if (authState.isAuthenticated && !authState.isLoading) {
        validateToken();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [authState.isAuthenticated, authState.isLoading, validateToken]);

  useEffect(() => {
    if (!authState.isAuthenticated) {
      previousUserSubRef.current = null;
      return;
    }
    const currentUserSub = authState.auth0User?.sub || null;
    if (currentUserSub && previousUserSubRef.current && previousUserSubRef.current !== currentUserSub) {
      TokenValidationService.resetValidation();
      setShowExpiryNotification(false);
      setShowExpiryWarning(false);
      setTimeUntilExpiry(null);
      setValidationIntervalMs(NORMAL_TOKEN_CHECK_MS);
      setSessionExpired(false);
      setAuthError(null);
      setProfileSyncCompleted(false);
      setNeedsProfileCompletion(false);
      profileSyncKeyRef.current = null;
      tokenRef.current.value = null;
      tokenRef.current.acquiredAt = 0;
      tokenRef.current.inFlightPromise = null;
      bootstrapStartRef.current = nowMs();
      setAuthTransitionCount(0);
      setAuthBootstrapDurationMs(null);
      lifecycleStateRef.current = AUTH_LIFECYCLE_STATES.BOOTING;
      setAuthLifecycleState(AUTH_LIFECYCLE_STATES.BOOTING);
    }
    previousUserSubRef.current = currentUserSub;
  }, [authState.isAuthenticated, authState.auth0User?.sub]);

  useEffect(() => {
    if (authState.isAuthenticated) return;
    setShowExpiryNotification(false);
    setShowExpiryWarning(false);
    setTimeUntilExpiry(null);
    setValidationIntervalMs(NORMAL_TOKEN_CHECK_MS);
    setSessionExpired(false);
    setAuthError(null);
    setNeedsProfileCompletion(false);
    setIsProfileSyncing(false);
    setProfileSyncCompleted(false);
    profileSyncKeyRef.current = null;
    tokenRef.current.value = null;
    tokenRef.current.acquiredAt = 0;
    tokenRef.current.inFlightPromise = null;
    TokenValidationService.resetValidation();
  }, [authState.isAuthenticated]);

  useEffect(() => {
    if (authState.isLoading || !authState.isAuthenticated || !authState.auth0User?.sub) {
      return undefined;
    }

    const profileSyncKey = `${authState.auth0User.sub}`;
    if (profileSyncKeyRef.current === profileSyncKey) {
      return undefined;
    }
    profileSyncKeyRef.current = profileSyncKey;

    let isCancelled = false;
    const syncProfile = async () => {
      setIsProfileSyncing(true);
      setProfileSyncCompleted(false);
      setProfileSyncCount((count) => count + 1);
      try {
        const token = await getProviderTokenOrThrow();
        const authConfig = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const dbBuyer = await checkBuyerExists(authState.auth0User.sub, authConfig);
        if (isCancelled) return;

        if (dbBuyer) {
          const needsCompletion = !isProfileComplete(dbBuyer);
          setNeedsProfileCompletion(needsCompletion);
          log.info(
            `[AuthStateProvider:syncProfile] > [Request]: checkBuyerExists > [Response]: found > [Comment]: profileComplete=${!needsCompletion}`
          );
          return;
        }

        const { firstName, lastName } = extractNameParts(authState.auth0User);
        const result = await syncAuth0Buyer({
          auth0Id: authState.auth0User.sub,
          email: authState.auth0User.email,
          firstName,
          lastName,
          source: 'Auth0 Signup',
        }, authConfig);
        if (isCancelled) return;

        setNeedsProfileCompletion(false);
        log.info(
          `[AuthStateProvider:syncProfile] > [Request]: syncAuth0Buyer > [Response]: created > [Comment]: buyerId=${result?.buyer?.id || 'unknown'}`
        );
      } catch (error) {
        if (isCancelled) return;
        setNeedsProfileCompletion(true);
        log.error(
          `[AuthStateProvider:syncProfile] > [Request]: sync profile > [Response]: error=${error?.message || 'unknown'}`,
          error
        );
      } finally {
        if (!isCancelled) {
          setIsProfileSyncing(false);
          setProfileSyncCompleted(true);
        }
      }
    };

    syncProfile();
    return () => {
      isCancelled = true;
    };
  }, [
    authState.isAuthenticated,
    authState.isLoading,
    authState.auth0User,
    authState.userPermissions,
    authState.userRoles,
    getProviderTokenOrThrow,
  ]);

  const derivedLifecycleState = useMemo(() => {
    if (authState.isLoading) {
      return AUTH_LIFECYCLE_STATES.BOOTING;
    }
    if (!authState.isAuthenticated) {
      return AUTH_LIFECYCLE_STATES.UNAUTHENTICATED;
    }
    if (sessionExpired) {
      return AUTH_LIFECYCLE_STATES.SESSION_EXPIRED;
    }
    if (authError) {
      return AUTH_LIFECYCLE_STATES.ERROR;
    }
    if (isValidatingToken) {
      return AUTH_LIFECYCLE_STATES.TOKEN_REFRESHING;
    }
    if (isProfileSyncing) {
      return AUTH_LIFECYCLE_STATES.PROFILE_SYNCING;
    }
    if (!profileSyncCompleted) {
      return AUTH_LIFECYCLE_STATES.TOKEN_READY;
    }
    return AUTH_LIFECYCLE_STATES.READY;
  }, [
    authState.isAuthenticated,
    authState.isLoading,
    authError,
    isProfileSyncing,
    isValidatingToken,
    profileSyncCompleted,
    sessionExpired,
  ]);

  useEffect(() => {
    if (lifecycleStateRef.current === derivedLifecycleState) {
      return;
    }

    const previousState = lifecycleStateRef.current;
    lifecycleStateRef.current = derivedLifecycleState;
    setAuthLifecycleState(derivedLifecycleState);
    setAuthTransitionCount((count) => count + 1);

    const elapsedMs = Math.round(nowMs() - bootstrapStartRef.current);
    log.info(
      `[AuthStateProvider:transition] > [Request]: transition state > [Response]: state=${derivedLifecycleState} > [Comment]: previous=${previousState} elapsedMs=${elapsedMs}`
    );

    if (authBootstrapDurationMs == null && RESOLVED_STATES.has(derivedLifecycleState)) {
      setAuthBootstrapDurationMs(elapsedMs);
      log.info(
        `[AuthStateProvider:transition] > [Request]: bootstrap timer > [Response]: resolved > [Comment]: durationMs=${elapsedMs}`
      );
    }
  }, [authBootstrapDurationMs, derivedLifecycleState]);

  const authResolved = RESOLVED_STATES.has(authLifecycleState);
  const authReadyForProtectedRoutes =
    authState.isAuthenticated &&
    !authState.isLoading &&
    !sessionExpired &&
    authLifecycleState !== AUTH_LIFECYCLE_STATES.ERROR;

  const providerValue = useMemo(
    () => ({
      ...authState,
      auth0User: authState.auth0User ? { ...authState.auth0User, needsProfileCompletion } : null,
      needsProfileCompletion,
      getToken: getProviderToken,
      getTokenOrThrow: getProviderTokenOrThrow,
      validateToken,
      isValidatingToken,
      authLifecycleState,
      authResolved,
      authReadyForProtectedRoutes,
      authTransitionCount,
      authBootstrapDurationMs,
      tokenAcquisitionCount,
      profileSyncCount,
      authError,
      sessionExpired,
      showExpiryWarning,
      timeUntilExpiry,
    }),
    [
      authBootstrapDurationMs,
      authError,
      authLifecycleState,
      authReadyForProtectedRoutes,
      authResolved,
      authState,
      authTransitionCount,
      getProviderToken,
      getProviderTokenOrThrow,
      isValidatingToken,
      needsProfileCompletion,
      profileSyncCount,
      sessionExpired,
      tokenAcquisitionCount,
      validateToken,
      showExpiryWarning,
      timeUntilExpiry,
    ]
  );

  return (
    <AuthStateContext.Provider value={providerValue}>
      {children}
      {showExpiryWarning && (
        <SessionExpiryWarningModal
          timeUntilExpiry={timeUntilExpiry}
          onDismiss={handleDismissWarning}
          onExtendSession={handleExtendSession}
          onLogout={authState.logout}
        />
      )}
      {showExpiryNotification && (
        <TokenExpiryNotification onClose={handleCloseNotification} />
      )}
    </AuthStateContext.Provider>
  );
};
